import React, { createContext, useContext, ReactNode } from 'react';

// ImageKit configuration interface
export interface IImageKitConfig {
  urlEndpoint: string; // Required: Your ImageKit URL endpoint
  publicKey?: string; // Optional: For file uploads
  authenticationEndpoint?: string; // Optional: For server-side authentication
  transformationPosition?: 'path' | 'query'; // Optional: Default transformation position
  defaultOptimization?: {
    quality?: number;
    background?: string;
    output?: 'jpg' | 'webp' | 'avif' | 'png';
    progressive?: boolean;
    lossless?: boolean;
  };
  deviceBreakpoints?: number[];
  imageBreakpoints?: number[];
}

// Default configuration
const defaultConfig: Partial<IImageKitConfig> = {
  transformationPosition: 'path',
  defaultOptimization: {
    quality: 85,
    background: 'FFFFFF',
    output: 'webp',
    progressive: true,
    lossless: false,
  },
  deviceBreakpoints: [640, 750, 828, 1080, 1200, 1920, 2048, 3840],
  imageBreakpoints: [16, 32, 48, 64, 96, 128, 256, 384],
};

// Create the context
const ImageKitContext = createContext<IImageKitConfig | null>(null);

// Provider component interface
export interface IImageKitProviderProps {
  config: IImageKitConfig;
  children: ReactNode;
}

// Provider component
export const ImageKitProvider: React.FC<IImageKitProviderProps> = ({
  config,
  children,
}) => {
  // Merge user config with defaults
  const mergedConfig: IImageKitConfig = {
    ...defaultConfig,
    ...config,
    defaultOptimization: {
      ...defaultConfig.defaultOptimization,
      ...config.defaultOptimization,
    },
  };

  return (
    <ImageKitContext.Provider value={mergedConfig}>
      {children}
    </ImageKitContext.Provider>
  );
};

// Hook to use ImageKit config
export const useImageKitConfig = (): IImageKitConfig => {
  const context = useContext(ImageKitContext);
  
  if (!context) {
    throw new Error(
      'useImageKitConfig must be used within an ImageKitProvider. ' +
      'Please wrap your component tree with <ImageKitProvider config={{urlEndpoint: "your-endpoint"}} />'
    );
  }
  
  return context;
};

// Hook to get ImageKit URL endpoint (convenience hook)
export const useImageKitEndpoint = (): string => {
  const config = useImageKitConfig();
  return config.urlEndpoint;
};

// Hook to get default optimization settings
export const useImageKitOptimization = () => {
  const config = useImageKitConfig();
  return config.defaultOptimization || defaultConfig.defaultOptimization;
};

// Hook to get device breakpoints
export const useImageKitBreakpoints = () => {
  const config = useImageKitConfig();
  return {
    deviceBreakpoints: config.deviceBreakpoints || defaultConfig.deviceBreakpoints,
    imageBreakpoints: config.imageBreakpoints || defaultConfig.imageBreakpoints,
  };
};

export default ImageKitProvider; 